import { useEffect, useState } from "react";
import wooStoreApiService from "@/api/services/wooStoreApiService";
import useAppDispatch from "@/hooks/useAppDispatch";
import { handleApiError } from "@/helpers/apiErrorHelper";
import { ProductCategoryName, StockStatus, type Product } from "@/types/products";

type ProductsByCategory = Record<ProductCategoryName, Product[]>;

interface HomePageViewModel {
  productsByCategory: ProductsByCategory | null;
  isLoadingProducts: boolean;
}

const useHomePage = (): HomePageViewModel => {
  const appDispatch = useAppDispatch();

  const [productsByCategory, setProductsByCategory] = useState<ProductsByCategory | null>(null);
  const [isLoadingProducts, setIsLoadingProducts] = useState<boolean>(true);

  const getProducts = async (signal?: AbortSignal) => {
    setIsLoadingProducts(true);

    const params = {
      orderby: "date",
      order: "desc",
      stock_status: [StockStatus.InStock],
      per_page: 8,
    };

    const categoryList = [ProductCategoryName.FloralFruity, ProductCategoryName.Nutty];

    try {
      const categoryPromises = categoryList.map((category) =>
        wooStoreApiService.product.getList({ ...params, category }, signal),
      );

      const results = await Promise.allSettled(categoryPromises);

      const mapProductsToCategories = categoryList.reduce((acc, category, index) => {
        const result = results[index];
        if (result.status === "rejected") {
          // .allSettled 永遠回應一個陣列值不會進 catch
          handleApiError(result.reason, appDispatch, "取得商品發生錯誤，請重新整理頁面再試一次");
        }
        acc[category] = result.status === "fulfilled" ? result.value.data : [];
        return acc;
      }, {} as ProductsByCategory);

      setProductsByCategory(mapProductsToCategories);
    } catch (error) {
      // 錯誤已在 Promise.allSettled 的 result.reason 中處理
    } finally {
      if (signal?.aborted) return;
      setIsLoadingProducts(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    getProducts(controller.signal);

    return () => {
      controller.abort();
    };
  }, []);

  return { productsByCategory, isLoadingProducts };
};

export default useHomePage;
